/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import SalesMatchingContext from './SalesMatchingContext';
import initialSalesMatchingState from './initialSalesMatchingState';
import salesMatchingReducer from 'contexts/salesMatching/reducer/salesMatching.reducer';
import * as action from 'contexts/salesMatching/reducer/salesMatching.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import {
  IaddToSalesMatchingToUpdateBody,
  ImatchSaleProps,
  IsetSortByProps,
} from 'interfaces/salesMatching.interface';
import { abortCatalogController } from 'utils/abortController';
import { useTranslation } from 'react-i18next';
import { IuseTableColumnSearchV2PropsSearchItems } from 'hooks/useTableColumnSearchV2';
import { DropResult } from 'react-beautiful-dnd';
import {
  IgetSalesMatchingDataItem,
  ImatchSaleAPIBodyItem,
} from 'api/endpoints/salesMatching/salesMatching.response.interface';

function SalesMatchingProvider(props: ChildrenProps) {
  const [salesMatchingState, dispatch] = useReducer(
    salesMatchingReducer,
    initialSalesMatchingState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  const getSalesMatchingItems = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    const signal = await abortCatalogController();
    return await action.getSalesMatchingItemsAction({
      dispatch,
      salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
      clientId: company.id,
      signal,
      token,
      translate,
    });
  }, [company?.id]);

  const setSalesMatchingItemsSelected = useCallback(
    (items: Array<IgetSalesMatchingDataItem>) => {
      return action.setSalesMatchingItemsSelectedAction({
        dispatch,
        items,
      });
    },
    []
  );

  const searchOnCatalogItems = useCallback(
    async ({
      searchField,
      searchText,
      searchType,
      type,
    }: IuseTableColumnSearchV2PropsSearchItems) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.searchOnCatalogItemsAction({
        dispatch,
        catalogBodyFetch: salesMatchingState.salesMatchingBodyFetch,
        searchOnCatalogItemsProps: {
          searchField,
          searchText,
          searchType,
          type,
        },
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState]
  );

  const setSortBy = useCallback(
    async ({ type, value }: IsetSortByProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.setSortByAction({
        dispatch,
        salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
        type,
        value,
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState.salesMatchingBodyFetch]
  );

  const addToSalesMatchingToUpdate = useCallback(
    async (body: IaddToSalesMatchingToUpdateBody) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addToSalesMatchingToUpdateAction({
        dispatch,
        body,
        salesMatchingToUpdate: salesMatchingState.salesMatchingToUpdate,
        options: salesMatchingState.options,
        token,
        translate,
      });
    },
    [salesMatchingState.salesMatchingToUpdate, salesMatchingState.options]
  );

  const matchSale = useCallback(
    async ({ brand_product, catalog_sale }: ImatchSaleProps) => {
      const token = await getAccessTokenSilently();
      if (!token)
        return {
          success: false,
        };
      const signal = await abortCatalogController();
      return await action.matchSaleAction({
        dispatch,
        brand_product,
        catalog_sale,
        salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
        salesMatchingItems: salesMatchingState.salesMatchingItems,
        salesMatchingToUpdateBody: salesMatchingState.salesMatchingToUpdateBody,
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState]
  );

  const matchSaleSimilarItems = useCallback(
    async (similarItemsBody: ImatchSaleAPIBodyItem[]) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.matchSaleSimilarItemsAction({
        dispatch,
        salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
        similarItemsBody,
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState.salesMatchingBodyFetch]
  );

  const cancelMatchSale = useCallback(
    (sale_id: string) => {
      return action.cancelMatchSaleAction({
        dispatch,
        sale_id,
        salesMatchingToUpdate: salesMatchingState.salesMatchingToUpdate,
      });
    },
    [salesMatchingState]
  );

  const unmatchSales = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    const signal = await abortCatalogController();
    return await action.unmatchSalesAction({
      dispatch,
      salesMatchingItemsSelected: salesMatchingState.salesMatchingItemsSelected,
      salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
      signal,
      token,
      translate,
    });
  }, [
    salesMatchingState.salesMatchingItemsSelected,
    salesMatchingState.salesMatchingBodyFetch,
  ]);

  const handlePagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.handlePaginationAction({
        dispatch,
        page,
        pageSize,
        salesMatchingState,
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState]
  );

  const setOwnClientProduct = useCallback(
    async (isOwn: boolean) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.setOwnClientProductAction({
        dispatch,
        salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
        isOwn,
        salesMatchingItemsSelected: salesMatchingState.salesMatchingItemsSelected,
        signal,
        token,
        translate,
      });
    },
    [salesMatchingState.salesMatchingItemsSelected]
  );

  // Columns //////////////////
  const moveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.moveColumnsOrderAction({
        dispatch,
        result,
        columnsOrder: salesMatchingState.columnsOrder,
      });
    },
    [salesMatchingState.columnsOrder]
  );

  const checkColumn = useCallback(
    (columnKey: string) => {
      return action.checkColumnAction({
        dispatch,
        columnKey,
        columnsOrder: salesMatchingState.columnsOrder,
      });
    },
    [salesMatchingState.columnsOrder]
  );

  const memoProvider = useMemo(
    () => ({
      ...salesMatchingState,
      getSalesMatchingItems,
      setSalesMatchingItemsSelected,
      searchOnCatalogItems,
      setSortBy,
      addToSalesMatchingToUpdate,
      matchSale,
      matchSaleSimilarItems,
      cancelMatchSale,
      unmatchSales,
      handlePagination,
      setOwnClientProduct,
      moveColumnsOrder,
      checkColumn,
    }),
    [
      salesMatchingState,
      getSalesMatchingItems,
      setSalesMatchingItemsSelected,
      searchOnCatalogItems,
      setSortBy,
      addToSalesMatchingToUpdate,
      matchSale,
      matchSaleSimilarItems,
      cancelMatchSale,
      unmatchSales,
      handlePagination,
      setOwnClientProduct,
      moveColumnsOrder,
      checkColumn,
    ]
  );

  return (
    <SalesMatchingContext.Provider value={memoProvider}>
      {props.children}
    </SalesMatchingContext.Provider>
  );
}

export default memo(SalesMatchingProvider);
